// Field — label + control + hint + error, all properly ARIA-wired.
//
// Wraps any single form control. The label is associated via htmlFor;
// hint and error are surfaced to screen readers via aria-describedby /
// aria-errormessage. Inputs flip aria-invalid when `error` is set.

import {
  cloneElement,
  isValidElement,
  type ReactElement,
  useId,
} from "react";

import { tokens } from "../tokens/tokens";

export interface FieldProps {
  /** Visible label above the control. */
  label: string;
  /** Optional helper text under the control. */
  hint?: string;
  /** Error message — when present, the control is marked aria-invalid. */
  error?: string;
  /** Mark the label as required ("*"). Set required on the input too. */
  required?: boolean;
  /** The control — a native <input>, <select>, <textarea>, or a custom
   *  component that forwards id + aria-* attributes. */
  children: ReactElement<{
    id?: string;
    "aria-describedby"?: string;
    "aria-invalid"?: boolean | "true" | "false";
    "aria-errormessage"?: string;
  }>;
}

export function Field({ label, hint, error, required, children }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy =
    [hintId, errorId].filter(Boolean).join(" ") || undefined;

  const enriched = isValidElement(children)
    ? cloneElement(children, {
        id,
        "aria-describedby": describedBy,
        "aria-invalid": error ? "true" : undefined,
        "aria-errormessage": errorId,
      })
    : children;

  return (
    <div style={containerStyle}>
      <label htmlFor={id} style={labelStyle}>
        {label}
        {required && (
          <span aria-hidden="true" style={requiredMarkStyle}>
            {" "}
            *
          </span>
        )}
      </label>
      {enriched}
      {hint && !error && (
        <p id={hintId} style={hintStyle}>
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" style={errorStyle}>
          {error}
        </p>
      )}
    </div>
  );
}

const containerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: tokens.space[1],
};
const labelStyle: React.CSSProperties = {
  fontSize: tokens.type.size.sm,
  fontWeight: tokens.type.weight.medium,
  color: tokens.color.semantic.text,
};
const requiredMarkStyle: React.CSSProperties = {
  color: tokens.color.danger[500],
};
const hintStyle: React.CSSProperties = {
  fontSize: tokens.type.size.xs,
  color: tokens.color.semantic.textMuted,
};
const errorStyle: React.CSSProperties = {
  fontSize: tokens.type.size.xs,
  color: tokens.color.danger[700],
};
